/**
 * FERP Portal - Dynamic Routes Component
 *
 * Generates React Router routes from SubgraphMetadata configuration.
 *
 * FERP Principles:
 * - Data-driven: Route definitions come from backend (SubgraphMetadata)
 * - NO hardcoded subgraph-specific routes
 * - Life-critical: Routes can require special authentication
 *
 * Usage:
 * ```tsx
 * <Routes>
 *   {/* Static core routes *\/}
 *   <Route path="/dashboard" element={<DashboardPage />} />
 *
 *   {/* Dynamic subgraph routes *\/}
 *   <DynamicRoutes config={config} />
 * </Routes>
 * ```
 */

import React from 'react';
import { Route } from 'react-router-dom';

import type { TenantSubgraphConfig, SubgraphRoute } from '@/hooks/useTenantSubgraphConfig';
import { getComponent } from './componentMap';

interface DynamicRoutesProps {
  /** The tenant's subgraph configuration */
  config: TenantSubgraphConfig | null;
}

/**
 * Renders a single dynamic route.
 */
function DynamicRoute({ route }: { route: SubgraphRoute }) {
  const Component = getComponent(route.component);

  return (
    <Route
      key={route.path}
      path={route.path}
      element={<Component />}
    />
  );
}

/**
 * Generates React Router routes from SubgraphMetadata configuration.
 *
 * Routes are generated based on the tenant's enabled subgraphs.
 * Each route maps to a component registered in componentMap.ts.
 */
export function DynamicRoutes({ config }: DynamicRoutesProps): React.ReactElement[] {
  if (!config) {
    return [];
  }

  return config.allRoutes.map((route) => (
    <Route
      key={route.path}
      path={route.path}
      element={React.createElement(getComponent(route.component))}
    />
  ));
}

/**
 * Hook to get dynamic routes as JSX elements.
 *
 * Useful when you need to combine dynamic routes with static routes
 * in a Routes component.
 */
export function useDynamicRoutes(config: TenantSubgraphConfig | null): React.ReactElement[] {
  return React.useMemo(() => {
    if (!config) {
      return [];
    }

    return config.allRoutes.map((route) => (
      <Route
        key={route.path}
        path={route.path}
        element={React.createElement(getComponent(route.component))}
      />
    ));
  }, [config]);
}

export default DynamicRoutes;
